// Equipe.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import api from '../../services/api';
import toast, { Toaster } from 'react-hot-toast';
import { 
    UserPlus, X, Trash2, Edit, Calendar, Hash, ShieldCheck, 
    UserCircle, Mail, Image as ImageIcon, Check, Lock, 
    ChevronLeft, ChevronRight, Plus, Settings, GripVertical, 
    ExternalLink, RotateCw, Sparkles, User, Shield
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import '../../styles/Equipe.css';

// Ícone SVG do WhatsApp
const WhatsAppIcon = ({ size = 14, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
    </svg>
);

const Equipe = ({ estabelecimentoId }) => {
    const fileInputRef = useRef(null);
    const [funcionarios, setFuncionarios] = useState([]);
    const [lojaLogo, setLojaLogo] = useState('');
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editandoId, setEditandoId] = useState(null);
    const [flippedCards, setFlippedCards] = useState({});

    // --- CATEGORIAS VINCULADAS AO BANCO DE DADOS ---
    const [categorias, setCategorias] = useState([]);
    const [showCatModal, setShowCatModal] = useState(false);
    const [novaCatNome, setNovaCatNome] = useState('');
    const [editandoCatId, setEditandoCatId] = useState(null);

    // --- CONTROLE DE PLANO E LIMITES DE EQUIPE ---
    const [limiteEquipeMax, setLimiteEquipeMax] = useState(2);

    // --- ESTADOS DE FILTRO POR SETOR E PAGINAÇÃO ---
    const [filtroSetor, setFiltroSetor] = useState('todos'); 
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    // --- ESTADOS DO RECORTE (CROP) ---
    const [image, setImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [showCropper, setShowCropper] = useState(false);
    
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    
    // --- ESTADOS DE EXCLUSÃO ---
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemParaExcluir, setItemParaExcluir] = useState(null);

    const initialState = { 
        nome: '', 
        email: '', 
        whatsapp:'', 
        cargo: 'Atendente', 
        senha: '', 
        data_nascimento: '', 
        ativo: 1 
    };
    const [novoMembro, setNovoMembro] = useState(initialState);

    const carregarDados = async () => {
        if (!estabelecimentoId) return;
        try {
            const [resEquipe, resLoja, resCats] = await Promise.all([
                api.get(`/cardapio/equipe/${estabelecimentoId}`),
                api.get(`/cardapio/estabelecimento/${estabelecimentoId}`),
                api.get(`/cardapio/equipe/categorias/${estabelecimentoId}`)
            ]);
            
            setFuncionarios(Array.isArray(resEquipe.data) ? resEquipe.data : []);
            setLojaLogo(resLoja.data?.logo_url || '');
            setCategorias(Array.isArray(resCats.data) ? resCats.data : []);

            if (resCats.data && resCats.data.length > 0 && !novoMembro.cargo) {
                setNovoMembro(prev => ({ ...prev, cargo: resCats.data[0].nome }));
            }

            const limiteProd = Number(resLoja.data?.limite_produtos || 50);
            if (limiteProd <= 50) {
                setLimiteEquipeMax(2);     
            } else if (limiteProd <= 100) {
                setLimiteEquipeMax(10);    
            } else if (limiteProd <= 300) {
                setLimiteEquipeMax(20);    
            } else {
                setLimiteEquipeMax(50);    
            }

        } catch (err) { 
            toast.error("Erro ao sincronizar dados."); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { carregarDados(); }, [estabelecimentoId]);

    const isOnline = (ultimoLogin) => {
        if (!ultimoLogin) return false;
        const dezMinutosAtras = new Date(Date.now() - 10 * 60 * 1000);
        return new Date(ultimoLogin) > dezMinutosAtras;
    };

    const formatarWhatsapp = (num) => {
        if (!num) return 'Não informado';
        const limpo = String(num).replace(/\D/g, '');
        if (limpo.length === 11) {
            return `(${limpo.slice(0, 2)}) ${limpo.slice(2, 7)}-${limpo.slice(7)}`;
        } else if (limpo.length === 10) {
            return `(${limpo.slice(0, 2)}) ${limpo.slice(2, 6)}-${limpo.slice(6)}`;
        } else if (limpo.length === 13 && limpo.startsWith('55')) {
            return `(${limpo.slice(2, 4)}) ${limpo.slice(4, 9)}-${limpo.slice(9)}`;
        }
        return num;
    };

    const formatarLinkWa = (num) => {
        if (!num) return '';
        let limpo = String(num).replace(/\D/g, '');
        if (!limpo.startsWith('55') && limpo.length <= 11) {
            limpo = '55' + limpo;
        }
        return `https://wa.me/${limpo}`;
    };

    // --- GERENCIAMENTO DE CATEGORIAS NO BD ---
    const salvarCategoria = async (e) => {
        e.preventDefault();
        if (!novaCatNome.trim()) return;

        try {
            if (editandoCatId !== null) {
                await api.put(`/cardapio/equipe/categorias/${editandoCatId}`, { nome: novaCatNome.trim() });
                toast.success("Categoria atualizada com sucesso!");
            } else {
                await api.post(`/cardapio/equipe/categorias/${estabelecimentoId}`, { nome: novaCatNome.trim() });
                toast.success("Nova categoria criada!");
            }

            setNovaCatNome('');
            setEditandoCatId(null);
            carregarDados();
        } catch (err) {
            toast.error("Erro ao salvar categoria.");
        }
    };

    const excluirCategoria = async (catId, catNome) => {
        if (categorias.length <= 1) {
            return toast.error("Você precisa manter pelo menos uma categoria.");
        }
        try {
            await api.delete(`/cardapio/equipe/categorias/${catId}`);
            toast.success("Categoria removida.");
            if (filtroSetor.toLowerCase() === catNome.toLowerCase()) {
                setFiltroSetor('todos');
            }
            carregarDados();
        } catch (err) {
            toast.error("Erro ao excluir categoria.");
        }
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) return;

        const items = Array.from(categorias);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setCategorias(items);

        const payload = items.map((cat, index) => ({ id: cat.id, ordem: index }));

        try {
            await api.put(`/cardapio/equipe/categorias/ordenar/${estabelecimentoId}`, { itens: payload });
            toast.success("Ordem atualizada!");
        } catch (err) {
            toast.error("Erro ao salvar nova ordem.");
            carregarDados();
        }
    };

    // --- RECORTE DE IMAGEM ---
    const onCropComplete = useCallback((_area, pixels) => {
        setCroppedAreaPixels(pixels);
    }, []);

    const finalizarRecorte = async () => {
        try {
            const canvas = document.createElement('canvas');
            const img = new Image();
            img.src = image;
            await new Promise((res) => (img.onload = res));

            canvas.width = croppedAreaPixels.width;
            canvas.height = croppedAreaPixels.height;
            const ctx = canvas.getContext('2d');

            ctx.drawImage(
                img,
                croppedAreaPixels.x, croppedAreaPixels.y,
                croppedAreaPixels.width, croppedAreaPixels.height,
                0, 0,
                croppedAreaPixels.width, croppedAreaPixels.height
            );

            canvas.toBlob((blob) => {
                const file = new File([blob], "avatar.jpg", { type: "image/jpeg" });
                setUploadFile(file);
                setImagePreview(URL.createObjectURL(blob));
                setShowCropper(false);
            }, 'image/jpeg');
        } catch (e) {
            toast.error("Erro ao processar imagem.");
        }
    };

    const handleSalvar = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('estabelecimento_id', estabelecimentoId);
        formData.append('nome', novoMembro.nome);
        formData.append('email', novoMembro.email);
        formData.append('whatsapp', novoMembro.whatsapp);
        formData.append('cargo', novoMembro.cargo);
        formData.append('data_nascimento', novoMembro.data_nascimento || '');
        formData.append('ativo', novoMembro.ativo);
        
        if (novoMembro.senha) formData.append('senha', novoMembro.senha);
        if (uploadFile) formData.append('foto', uploadFile);

        try {
            if (editandoId) {
                await api.put(`/cardapio/equipe/${editandoId}`, formData);
                toast.success("Cartão digital atualizado!");
            } else {
                if (funcionarios.length >= limiteEquipeMax) {
                    return toast.error(`Limite de ${limiteEquipeMax} membros atingido para o seu plano atual.`);
                }
                await api.post(`/cardapio/equipe`, formData);
                toast.success("Cartão digital emitido!");
            }
            
            fecharModal();
            carregarDados();
        } catch (err) { 
            toast.error("Erro ao salvar dados."); 
        }
    };
        
    const executarExclusao = async () => {
        try {
            await api.delete(`/cardapio/equipe/${itemParaExcluir.id}`);
            toast.success("Acesso removido com sucesso!");
            setShowDeleteModal(false);
            setItemParaExcluir(null);
            carregarDados();
        } catch (err) { 
            toast.error("Erro ao remover membro da equipe."); 
        }
    };
                
    const confirmarExclusao = (func) => {
        setItemParaExcluir(func);
        setShowDeleteModal(true);
    };
    
    const fecharModal = () => {
        setShowModal(false);
        setEditandoId(null);
        setNovoMembro({ ...initialState, cargo: categorias[0]?.nome || 'Atendente' });
        setImagePreview(null);
        setUploadFile(null);
        setImage(null);
        setShowCropper(false);
    };

    // --- FILTROS E PAGINAÇÃO ---
    const funcionariosFiltrados = funcionarios.filter(func => {
        if (filtroSetor === 'todos') return true;
        return func.cargo?.toLowerCase() === filtroSetor.toLowerCase();
    });

    const totalPages = Math.ceil(funcionariosFiltrados.length / itemsPerPage) || 1;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentFuncionarios = funcionariosFiltrados.slice(startIndex, startIndex + itemsPerPage);

    if (loading) return <div className="loader-container"><div className="spinner"></div></div>;

    return (
        <div className="tab-content animate-fade-in" style={{ padding: '20px 0' }}>
            <Toaster position="top-right" />
            
            {/* CABEÇALHO */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>
                        <Sparkles size={24} color="#ea1d2c" /> Equipe Digital ({funcionarios.length}/{limiteEquipeMax})
                    </h2>
                    <p style={{ color: '#64748b', fontSize: '13.5px', margin: '4px 0 0 0' }}>
                        Crachás digitais interativos com credenciais e WhatsApp integrado.
                    </p>
                </div>
                
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                        className="btn-confirm" 
                        style={{ background: '#334155', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', borderRadius: '10px', fontWeight: '600' }}
                        onClick={() => setShowCatModal(true)}
                    >
                        <Settings size={18} /> Gerenciar Categorias
                    </button>
                    <button 
                        className="btn-confirm" 
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', borderRadius: '10px', fontWeight: '700', background: '#ea1d2c' }}
                        onClick={() => {
                            if (funcionarios.length >= limiteEquipeMax) {
                                toast.error(`Seu plano atual permite até ${limiteEquipeMax} membros.`);
                                return;
                            }
                            setShowModal(true);
                        }}
                    >
                        <UserPlus size={18} /> Novo Cartão
                    </button>
                </div>
            </div>

            {/* SELETORES DE CATEGORIAS */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '25px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', overflowX: 'auto' }}>
                <button
                    onClick={() => { setFiltroSetor('todos'); setCurrentPage(1); }}
                    style={{
                        background: filtroSetor === 'todos' ? '#ea1d2c' : '#f8fafc',
                        color: filtroSetor === 'todos' ? '#fff' : '#64748b',
                        border: '1px solid #e2e8f0',
                        padding: '8px 18px',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease',
                        boxShadow: filtroSetor === 'todos' ? '0 4px 10px rgba(234, 29, 44, 0.25)' : 'none'
                    }}
                >
                    Todos os Membros ({funcionarios.length})
                </button>
                {categorias.map(cat => {
                    const count = funcionarios.filter(f => f.cargo?.toLowerCase() === cat.nome.toLowerCase()).length;
                    const isActive = filtroSetor === cat.nome.toLowerCase();
                    return (
                        <button
                            key={cat.id}
                            onClick={() => { setFiltroSetor(cat.nome.toLowerCase()); setCurrentPage(1); }}
                            style={{
                                background: isActive ? '#ea1d2c' : '#f8fafc',
                                color: isActive ? '#fff' : '#64748b',
                                border: '1px solid #e2e8f0',
                                padding: '8px 18px',
                                borderRadius: '20px',
                                fontSize: '13px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                textTransform: 'capitalize',
                                transition: 'all 0.2s ease',
                                boxShadow: isActive ? '0 4px 10px rgba(234, 29, 44, 0.25)' : 'none'
                            }}
                        >
                            {cat.nome} ({count})
                        </button>
                    );
                })}
            </div>

            {/* GRID DE CARTÕES DIGITAIS */}
            {currentFuncionarios.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', color: '#94a3b8' }}>
                    <UserCircle size={48} style={{ opacity: 0.4, marginBottom: '10px' }} />
                    <p style={{ margin: 0, fontSize: '15px' }}>Nenhum membro encontrado nesta categoria.</p>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: '24px' }}>
                    {currentFuncionarios.map(func => {
                        const online = isOnline(func.ultimo_login);
                        const isFlipped = !!flippedCards[func.id];

                        return (
                            <div 
                                key={func.id} 
                                style={{
                                    height: '215px',
                                    perspective: '1200px',
                                    cursor: 'pointer',
                                    position: 'relative'
                                }}
                                onClick={() => setFlippedCards(p => ({ ...p, [func.id]: !p[func.id] }))}
                            >
                                <div style={{
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transformStyle: 'preserve-3d',
                                    transform: isFlipped ? 'rotateY(180deg)' : 'none'
                                }}>
                                    
                                    {/* ======================================================== */}
                                    {/* FRENTE DO CARTÃO DIGITAL                                */}
                                    {/* ======================================================== */}
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '16px',
                                        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)',
                                        border: '1px solid rgba(255, 255, 255, 0.12)',
                                        color: '#ffffff',
                                        padding: '16px 18px',
                                        boxSizing: 'border-box',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.3)',
                                        backfaceVisibility: 'hidden',
                                        WebkitBackfaceVisibility: 'hidden',
                                        overflow: 'hidden'
                                    }}>
                                        {/* Marca d'água */}
                                        {lojaLogo && (
                                            <img 
                                                src={lojaLogo} 
                                                alt="watermark" 
                                                style={{ position: 'absolute', right: '-15px', bottom: '-15px', width: '130px', height: '130px', objectFit: 'contain', opacity: 0.04, pointerEvents: 'none' }} 
                                            />
                                        )}

                                        {/* TOPO: CHIP DOURADO + DICA + BOTÃO EDITAR */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                            <div style={{
                                                width: '34px',
                                                height: '24px',
                                                background: 'linear-gradient(135deg, #fef08a 0%, #ca8a04 50%, #eab308 100%)',
                                                borderRadius: '5px',
                                                border: '1px solid rgba(255, 255, 255, 0.4)',
                                                boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.5), 0 2px 4px rgba(0,0,0,0.25)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-around',
                                                padding: '3px 4px',
                                                boxSizing: 'border-box'
                                            }}>
                                                <div style={{ width: '100%', height: '1px', background: 'rgba(0,0,0,0.3)' }}></div>
                                                <div style={{ width: '100%', height: '1px', background: 'rgba(0,0,0,0.3)' }}></div>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', zIndex: 10 }}>
                                                <span style={{ fontSize: '10.5px', color: '#94a3b8', background: 'rgba(255,255,255,0.08)', padding: '3px 8px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    <RotateCw size={11} /> Toque p/ virar
                                                </span>

                                                <button 
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditandoId(func.id);
                                                        const dataFormatada = func.data_nascimento ? func.data_nascimento.split('T')[0] : '';
                                                        setNovoMembro({ ...func, data_nascimento: dataFormatada, senha: '' });
                                                        setImagePreview(func.foto_url);
                                                        setShowModal(true);
                                                    }}
                                                    style={{
                                                        background: 'rgba(255,255,255,0.12)',
                                                        border: '1px solid rgba(255,255,255,0.2)',
                                                        color: '#ffffff',
                                                        width: '28px',
                                                        height: '28px',
                                                        borderRadius: '8px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer',
                                                        transition: '0.2s'
                                                    }}
                                                    title="Editar dados"
                                                >
                                                    <Edit size={14}/>
                                                </button>
                                            </div>
                                        </div>

                                        {/* MEIO: FOTO + NOME + CARGO + WHATSAPP */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', margin: '4px 0' }}>
                                            <div style={{
                                                width: '54px',
                                                height: '54px',
                                                borderRadius: '50%',
                                                padding: '2px',
                                                background: online ? 'linear-gradient(135deg, #22c55e, #16a34a)' : '#475569',
                                                position: 'relative',
                                                flexShrink: 0
                                            }}>
                                                <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {func.foto_url ? (
                                                        <img src={func.foto_url} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        <User size={28} color="#94a3b8" />
                                                    )}
                                                </div>
                                                <span style={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    right: 0,
                                                    width: '12px',
                                                    height: '12px',
                                                    borderRadius: '50%',
                                                    border: '2px solid #0f172a',
                                                    background: online ? '#22c55e' : '#94a3b8'
                                                }} />
                                            </div>

                                            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                                <div style={{ fontSize: '16px', fontWeight: '800', color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: '0.3px' }} title={func.nome}>
                                                    {func.nome}
                                                </div>
                                                
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                                                    <span style={{
                                                        fontSize: '10px',
                                                        fontWeight: '800',
                                                        letterSpacing: '0.5px',
                                                        color: '#fca5a5',
                                                        background: 'rgba(234, 29, 44, 0.25)',
                                                        border: '1px solid rgba(234, 29, 44, 0.5)',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        {func.cargo}
                                                    </span>

                                                    {func.whatsapp && (
                                                        <span style={{ fontSize: '11px', color: '#86efac', display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: '500' }}>
                                                            <WhatsAppIcon size={12} color="#22c55e" /> {formatarWhatsapp(func.whatsapp)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* RODAPÉ: DATA DE EMISSÃO + STATUS */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingTop: '8px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                            <div>
                                                <div style={{ fontSize: '9px', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.5px' }}>DATA DE EMISSÃO</div>
                                                <div style={{ fontSize: '12px', fontWeight: '600', color: '#e2e8f0', marginTop: '1px' }}>
                                                    {func.criado_em ? new Date(func.criado_em).toLocaleDateString() : '---'}
                                                </div>
                                            </div>

                                            <div style={{
                                                padding: '3px 10px',
                                                borderRadius: '12px',
                                                fontSize: '10px',
                                                fontWeight: '800',
                                                letterSpacing: '0.5px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                color: '#ffffff',
                                                background: func.ativo ? '#16a34a' : '#dc2626'
                                            }}>
                                                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#ffffff' }} />
                                                {func.ativo ? 'ATIVO' : 'BLOQUEADO'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* ======================================================== */}
                                    {/* VERSO DO CARTÃO DIGITAL (PERFEITAMENTE ENQUADRADO)     */}
                                    {/* ======================================================== */}
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '16px',
                                        background: '#ffffff',
                                        border: '1px solid #e2e8f0',
                                        color: '#0f172a',
                                        padding: '14px 16px',
                                        boxSizing: 'border-box',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.15)',
                                        transform: 'rotateY(180deg)',
                                        backfaceVisibility: 'hidden',
                                        WebkitBackfaceVisibility: 'hidden',
                                        overflow: 'hidden'
                                    }}>
                                        {/* TOPO DO VERSO */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '6px' }}>
                                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <ShieldCheck size={16} color="#00a884" /> Credencial de Acesso
                                            </div>
                                            <span style={{ fontSize: '10px', fontWeight: '600', color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                                                ID: #{func.id}
                                            </span>
                                        </div>

                                        {/* CORPO DE DADOS DO VERSO */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', margin: '4px 0' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '5px 8px', borderRadius: '6px', border: '1px solid #f1f5f9', fontSize: '11.5px', color: '#334155' }}>
                                                <Mail size={14} color="#64748b" style={{ flexShrink: 0 }} /> 
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={func.email}>{func.email}</span>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f0fdf4', border: '1px solid #dcfce7', color: '#15803d', padding: '5px 8px', borderRadius: '6px', fontSize: '11.5px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                                                    <WhatsAppIcon size={14} color="#16a34a" />
                                                    <strong style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatarWhatsapp(func.whatsapp)}</strong>
                                                </div>
                                                {func.whatsapp && (
                                                    <a 
                                                        href={formatarLinkWa(func.whatsapp)} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{ color: '#16a34a', display: 'flex', alignItems: 'center', padding: '2px 4px', textDecoration: 'none' }}
                                                        title="Abrir WhatsApp"
                                                    >
                                                        <ExternalLink size={12} />
                                                    </a>
                                                )}
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '5px 8px', borderRadius: '6px', border: '1px solid #f1f5f9', fontSize: '11.5px', color: '#334155' }}>
                                                <Calendar size={14} color="#64748b" style={{ flexShrink: 0 }} /> 
                                                <span>Nasc: {func.data_nascimento ? new Date(func.data_nascimento).toLocaleDateString() : 'Não informado'}</span>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '5px 8px', borderRadius: '6px', border: '1px solid #f1f5f9', fontSize: '11.5px', color: '#334155' }}>
                                                <Lock size={14} color={func.ativo ? "#16a34a" : "#ef4444"} style={{ flexShrink: 0 }} /> 
                                                <span>Acesso: <strong style={{ color: func.ativo ? '#16a34a' : '#ef4444' }}>{func.ativo ? 'Liberado' : 'Suspenso'}</strong></span>
                                            </div>
                                        </div>

                                        {/* BOTÃO EXCLUIR */}
                                        <button 
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                confirmarExclusao(func);
                                            }}
                                            style={{
                                                background: '#fee2e2',
                                                color: '#ef4444',
                                                border: '1px solid #fecaca',
                                                padding: '6px 10px',
                                                borderRadius: '8px',
                                                fontSize: '11px',
                                                fontWeight: '700',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                                cursor: 'pointer',
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                transition: '0.2s'
                                            }}
                                        >
                                            <Trash2 size={13} /> Remover Acesso do Colaborador
                                        </button>
                                    </div>

                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* PAGINAÇÃO */}
            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginTop: '30px' }}>
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
                    >
                        <ChevronLeft size={20} color="#334155" />
                    </button>
                    
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#334155' }}>
                        Página {currentPage} de {totalPages}
                    </span>

                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
                    >
                        <ChevronRight size={20} color="#334155" />
                    </button>
                </div>
            )}

            {/* MODAL GERENCIAR CATEGORIAS */}
            {showCatModal && (
                <div className="modal-overlay" style={{ zIndex: 3500 }}>
                    <div className="modal" style={{ maxWidth: '450px', padding: '25px' }}>
                        <div className="modal-header-flex" style={{ marginBottom: '20px' }}>
                            <h2 style={{ margin: 0, fontSize: '18px', color: '#0f172a' }}>Gerenciar Categorias</h2>
                            <button onClick={() => { setShowCatModal(false); setEditandoCatId(null); setNovaCatNome(''); }} className="btn-close-modal-x"><X size={24}/></button>
                        </div>

                        <form onSubmit={salvarCategoria} style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                            <input 
                                className="input" 
                                style={{ flex: 1, margin: 0 }}
                                placeholder="Nome da categoria..." 
                                value={novaCatNome} 
                                onChange={e => setNovaCatNome(e.target.value)} 
                                required 
                            />
                            <button type="submit" className="btn-confirm" style={{ padding: '0 20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {editandoCatId !== null ? 'Salvar' : <><Plus size={18} /> Adicionar</>}
                            </button>
                        </form>

                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="categorias-list">
                                {(provided) => (
                                    <div 
                                        {...provided.droppableProps} 
                                        ref={provided.innerRef}
                                        style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '250px', overflowY: 'auto' }}
                                    >
                                        {categorias.map((cat, index) => (
                                            <Draggable key={cat.id.toString()} draggableId={cat.id.toString()} index={index}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: '10px 14px',
                                                            background: snapshot.isDragging ? '#f1f5f9' : '#f8fafc',
                                                            borderRadius: '10px',
                                                            border: '1px solid #e2e8f0',
                                                            boxShadow: snapshot.isDragging ? '0 4px 6px -1px rgba(0,0,0,0.1)' : 'none',
                                                            ...provided.draggableProps.style
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                            <div {...provided.dragHandleProps} style={{ cursor: 'grab', color: '#94a3b8', display: 'flex', alignItems: 'center' }}>
                                                                <GripVertical size={18} />
                                                            </div>
                                                            <span style={{ fontWeight: '600', fontSize: '14px', color: '#334155' }}>{cat.nome}</span>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            <button 
                                                                type="button" 
                                                                onClick={() => { setEditandoCatId(cat.id); setNovaCatNome(cat.nome); }}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '4px' }}
                                                                title="Editar"
                                                            >
                                                                <Edit size={16} />
                                                            </button>
                                                            <button 
                                                                type="button" 
                                                                onClick={() => excluirCategoria(cat.id, cat.nome)}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px' }}
                                                                title="Excluir"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </div>
                </div>
            )}

            {/* MODAL CADASTRO / EDIÇÃO */}
            {showModal && (
                <div className="modal-overlay" style={{ zIndex: 3000 }}>
                    <div className="modal" style={{ maxWidth: '550px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: '0', overflow: 'hidden', position: 'relative' }}>
                        
                        {showCropper && (
                            <div style={{ position:'absolute', inset:0, background:'#000', zIndex:100, display:'flex', flexDirection:'column' }}>
                                <div style={{ position:'relative', flex:1 }}>
                                    <Cropper 
                                        image={image} 
                                        crop={crop} 
                                        zoom={zoom} 
                                        aspect={1} 
                                        cropShape="round" 
                                        onCropChange={setCrop} 
                                        onCropComplete={onCropComplete} 
                                        onZoomChange={setZoom} 
                                    />
                                </div>
                                <div style={{ background:'#fff', padding:20, display:'flex', gap:15, alignItems:'center' }}>
                                    <input type="range" value={zoom} min={1} max={3} step={0.1} onChange={(e) => setZoom(e.target.value)} style={{ flex:1 }} />
                                    <button type="button" onClick={finalizarRecorte} className="btn-confirm" style={{ background:'#22c55e', display: 'flex', alignItems: 'center', gap: '6px' }}><Check size={18}/> OK</button>
                                </div>
                            </div>
                        )}

                        <div className="modal-header-flex" style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', margin: 0, flexShrink: 0 }}>
                            <h2 style={{ margin: 0, fontSize: '18px', color: '#0f172a' }}>{editandoId ? 'Editar Cartão Digital' : 'Emitir Novo Cartão Digital'}</h2>
                            <button type="button" onClick={fecharModal} className="btn-close-modal-x" style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24}/></button>
                        </div>

                        <div style={{ overflowY: 'auto', padding: '20px' }} className="custom-scrollbar">
                            <form onSubmit={handleSalvar}>
                                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                    <div onClick={() => fileInputRef.current.click()} style={{ width: '105px', height: '105px', borderRadius: '50%', margin: '0 auto', border: '2px dashed #cbd5e1', cursor: 'pointer', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                        {imagePreview ? <img src={imagePreview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="preview" /> : <ImageIcon size={32} color="#94a3b8" />}
                                    </div>
                                    <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={(e) => {
                                        const file = e.target.files[0];
                                        if(file){
                                            const reader = new FileReader();
                                            reader.readAsDataURL(file);
                                            reader.onload = () => { setImage(reader.result); setShowCropper(true); };
                                        }
                                    }} />
                                    <small style={{ display:'block', marginTop: 8, color:'var(--primary)', fontWeight:'bold', cursor:'pointer' }} onClick={() => fileInputRef.current.click()}>Alterar / Ajustar Foto</small>
                                </div>

                                {editandoId && (
                                    <div style={{ marginBottom: '20px', padding: '12px', background: novoMembro.ativo ? '#f0fdf4' : '#fef2f2', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${novoMembro.ativo ? '#bbf7d0' : '#fecaca'}` }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontSize: '13px', fontWeight: 'bold', color: novoMembro.ativo ? '#166534' : '#991b1b' }}>Status do Acesso</span>
                                            <span style={{ fontSize: '11px', color: '#64748b' }}>{novoMembro.ativo ? 'Colaborador com acesso liberado' : 'Acesso temporariamente suspenso'}</span>
                                        </div>
                                        <select 
                                            style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', fontWeight: 'bold', outline: 'none' }} 
                                            value={novoMembro.ativo} 
                                            onChange={e => setNovoMembro({ ...novoMembro, ativo: Number(e.target.value) })}
                                        >
                                            <option value={1}>✅ Ativo</option>
                                            <option value={0}>🚫 Bloqueado</option>
                                        </select>
                                    </div>
                                )}

                                <label className="label">Nome Completo</label>
                                <input className="input" placeholder="Ex: Marcelo Silva" value={novoMembro.nome} onChange={e => setNovoMembro({ ...novoMembro, nome: e.target.value })} required />

                                <label className="label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <WhatsAppIcon size={15} color="#25d366" /> WhatsApp (com DDD)
                                </label>
                                <input className="input" type="text" placeholder="Ex: 11999999999" value={novoMembro.whatsapp} onChange={e => setNovoMembro({ ...novoMembro, whatsapp: e.target.value })} required />
                                
                                <label className="label">E-mail de Login</label>
                                <input className="input" type="email" placeholder="email@exemplo.com" value={novoMembro.email} onChange={e => setNovoMembro({ ...novoMembro, email: e.target.value })} required />

                                <div className="input-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div>
                                        <label className="label">Data de Nascimento</label>
                                        <input className="input" type="date" value={novoMembro.data_nascimento} onChange={e => setNovoMembro({ ...novoMembro, data_nascimento: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="label">Categoria / Função</label>
                                        <select className="input" value={novoMembro.cargo} onChange={e => setNovoMembro({ ...novoMembro, cargo: e.target.value })}>
                                            {categorias.map(cat => (
                                                <option key={cat.id} value={cat.nome}>{cat.nome}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <label className="label">Senha de Acesso</label>
                                <input 
                                    className="input" 
                                    type="password" 
                                    value={novoMembro.senha} 
                                    onChange={e => setNovoMembro({ ...novoMembro, senha: e.target.value })} 
                                    placeholder={editandoId ? "Deixe em branco para manter a atual" : "Digite a senha..."} 
                                    required={!editandoId} 
                                />

                                <button type="submit" className="btn-save-confirm" style={{ width: '100%', marginTop: '20px', padding: '14px', fontSize: '15px', fontWeight: 'bold' }}>
                                    {editandoId ? 'Atualizar Cartão Digital' : 'Emitir Cartão Digital'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE EXCLUSÃO */}
            {showDeleteModal && (
                <div className="modal-overlay" style={{ zIndex: 4000 }}>
                    <div className="modal" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <div style={{ 
                            background: '#fee2e2', 
                            width: '60px', 
                            height: '60px', 
                            borderRadius: '50%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            margin: '0 auto 20px' 
                        }}>
                            <Trash2 size={30} color="#ef4444" />
                        </div>
                        
                        <h3 style={{ marginBottom: '10px', color: '#1e293b' }}>Confirmar Exclusão</h3>
                        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '25px', lineHeight: '1.5' }}>
                            Tem certeza que deseja remover o acesso de <strong>{itemParaExcluir?.nome}</strong>? Esta ação não pode ser desfeita.
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <button 
                                type="button" 
                                className="btn-cancel" 
                                onClick={() => setShowDeleteModal(false)}
                                style={{ padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: '600' }}
                            >
                                Cancelar
                            </button>
                            <button 
                                type="button" 
                                onClick={executarExclusao}
                                style={{ 
                                    padding: '10px', 
                                    borderRadius: '8px', 
                                    background: '#ef4444', 
                                    color: '#fff', 
                                    border: 'none', 
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                }}
                            >
                                Confirmar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Equipe;